/* assets/gcitypartners-entry-gateway.js — GCITYPARTNERS-ENTRY-RUNTIME-17
 * Canonical owner: gateway disclosure-card routing, share controls, Screen 00 dialog,
 * video lifecycle and motion readiness.
 * Non-owner: opportunity API rendering, Review Brief persistence, GProperty hydration,
 * product registry, formal submission or authorization.
 */
(function (window, document) {
  'use strict';

  var ROOT_SELECTOR =
    '[data-gcitypartners-page-system][data-gateway-role="entry-opportunity-capability-and-brief-router"]';
  var VERSION = 'gcitypartners-entry-router-21-public-tabs';
  var FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');
  var PROOF_ANCHORS = {
    'natural-resources': 'scenario-natural-resources',
    'land-natural-resources': 'scenario-natural-resources',
    'land-resources': 'scenario-natural-resources',
    'digital-city': 'scenario-digital-city',
    'digital-city-geospatial-analytics': 'scenario-digital-city',
    'urban-renewal': 'scenario-urban-renewal',
    'industrial-community': 'scenario-industrial-community',
    'industry-park': 'scenario-industrial-community',
    'industry-park-development': 'scenario-industrial-community',
    'industrial-park-planning-development': 'scenario-industrial-community',
    'asset-advisory': 'gproject-scenario-asset-appraisal-value',
    'property-assets': 'gproject-scenario-asset-appraisal-value',
    'property-assets-investment': 'gproject-scenario-asset-appraisal-value',
    'property-asset-valuation': 'gproject-scenario-asset-appraisal-value',
    'urban-construction': 'scenario-urban-construction',
    infrastructure: 'scenario-urban-construction',
    'infrastructure-engineering': 'scenario-urban-construction',
    'surveying-geomatics': 'scenario-urban-construction',
    environment: 'scenario-sustainability',
    sustainability: 'scenario-sustainability',
    'green-city-service': 'scenario-sustainability',
    'ecology-environmental-assessment': 'scenario-sustainability',
    water: 'scenario-water-marine',
    'water-marine': 'scenario-water-marine',
    'water-resilience': 'scenario-water-marine',
    'water-infrastructure-resilience': 'scenario-water-marine',
  };
  var ENTRY_ANCHORS = {
    'natural-resources': 'gcp-scene-land-natural-resources',
    'land-natural-resources': 'gcp-scene-land-natural-resources',
    'land-resources': 'gcp-scene-land-natural-resources',
    'digital-city': 'gcp-scene-digital-city',
    'digital-city-geospatial-analytics': 'gcp-scene-digital-city',
    'urban-renewal': 'gcp-scene-urban-renewal',
    'industrial-community': 'gcp-scene-industry-park-development',
    'industry-park': 'gcp-scene-industry-park-development',
    'industry-park-development': 'gcp-scene-industry-park-development',
    'industrial-park-planning-development': 'gcp-scene-industry-park-development',
    'asset-advisory': 'gcp-scene-property-assets-investment',
    'property-assets': 'gcp-scene-property-assets-investment',
    'property-assets-investment': 'gcp-scene-property-assets-investment',
    'property-asset-valuation': 'gcp-scene-property-assets-investment',
    'urban-construction': 'gcp-scene-infrastructure-engineering',
    infrastructure: 'gcp-scene-infrastructure-engineering',
    'infrastructure-engineering': 'gcp-scene-infrastructure-engineering',
    'surveying-geomatics': 'gcp-scene-infrastructure-engineering',
    environment: 'gcp-scene-green-city-service',
    sustainability: 'gcp-scene-green-city-service',
    'green-city-service': 'gcp-scene-green-city-service',
    'ecology-environmental-assessment': 'gcp-scene-green-city-service',
    water: 'gcp-scene-water-resilience',
    'water-marine': 'gcp-scene-water-resilience',
    'water-resilience': 'gcp-scene-water-resilience',
    'water-infrastructure-resilience': 'gcp-scene-water-resilience',
  };
  var REVIEW_NEEDS = {
    'asset-advisory': 'property-spatial-brief',
    'property-assets': 'property-spatial-brief',
    'property-assets-investment': 'property-spatial-brief',
    'property-asset-valuation': 'property-spatial-brief',
    'digital-city': 'spatial-survey-report',
    'digital-city-geospatial-analytics': 'spatial-survey-report',
    infrastructure: 'spatial-survey-report',
    'infrastructure-engineering': 'spatial-survey-report',
    'surveying-geomatics': 'spatial-survey-report',
  };

  function escapeSelector(value) {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(value);
    }
    return String(value || '').replace(/["\\]/g, '\\$&');
  }

  function normalizeScenario(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function isChinese() {
    return /^zh\b/i.test(document.documentElement.lang || '');
  }

  function isReducedMotion() {
    return Boolean(
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function hasSaveDataPreference() {
    var connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    return Boolean(connection && connection.saveData);
  }

  function createAbortController() {
    return typeof window.AbortController === 'function' ? new window.AbortController() : null;
  }

  function addFallbackCleanup(root, cleanup) {
    if (typeof cleanup !== 'function') return;
    if (!root.__gcpEntryFallbackCleanups) root.__gcpEntryFallbackCleanups = [];
    root.__gcpEntryFallbackCleanups.push(cleanup);
  }

  function listen(root, target, type, handler, options, controller) {
    if (!target || typeof target.addEventListener !== 'function') return;
    var baseOptions = options || false;
    var finalOptions = controller
      ? Object.assign({}, options || {}, { signal: controller.signal })
      : baseOptions;
    target.addEventListener(type, handler, finalOptions);
    if (!controller) {
      addFallbackCleanup(root, function () {
        target.removeEventListener(type, handler, baseOptions);
      });
    }
  }

  function announceShare(root, message) {
    var status = root.querySelector('[data-gcp-share-status]');
    if (status) status.textContent = message || '';
  }

  function stripTracking(url) {
    ['preview_theme_id', '_fd', 'pb', 'cache', 'section_id'].forEach(function (key) {
      url.searchParams.delete(key);
    });
    Array.from(url.searchParams.keys()).forEach(function (key) {
      if (/^utm_/i.test(key)) url.searchParams.delete(key);
    });
    return url;
  }

  function cleanShareUrl(trigger) {
    var directUrl = trigger.getAttribute('data-share-url') || '';
    if (directUrl) {
      try {
        return stripTracking(new URL(directUrl, window.location.origin)).toString();
      } catch (_) {}
    }
    var url = stripTracking(new URL(window.location.href));
    var card = trigger.closest('[data-gcp-entry-card]');
    var opportunity = card ? card.getAttribute('data-gcp-opportunity-entry') || '' : '';
    var scenario = card ? card.getAttribute('data-gcp-scene-handle') || '' : '';
    var anchor = trigger.getAttribute('data-share-anchor') || '';
    if (opportunity) {
      url.searchParams.set('type', opportunity);
      ['scenario', 'source', 'intent'].forEach(function (key) {
        url.searchParams.delete(key);
      });
      url.hash = 'opportunities';
    } else if (scenario) {
      url.searchParams.delete('type');
      url.searchParams.set('source', 'shared-route');
      url.searchParams.set('intent', 'service-route');
      url.searchParams.set('scenario', scenario);
      url.hash = 'gcp-scene-gateway';
    } else if (anchor) {
      url.hash = anchor;
    }
    return url.toString();
  }

  function copyText(value) {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(value);
    }
    return new Promise(function (resolve, reject) {
      var textarea = document.createElement('textarea');
      textarea.value = value;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      try {
        if (!document.execCommand('copy')) throw new Error('copy_failed');
        resolve();
      } catch (error) {
        reject(error);
      } finally {
        textarea.remove();
      }
    });
  }

  async function shareEntry(root, trigger) {
    var url = cleanShareUrl(trigger);
    var title = trigger.getAttribute('data-share-title') || document.title || 'GCityPartners';
    var isOpportunity = trigger.hasAttribute('data-gcp-share-opportunity');
    var text = isChinese()
      ? isOpportunity
        ? '這是一個已在 GCityPartners 顯示的機會，請以主辦方官方頁面的最新要求與截止狀態為準。'
        : '這個 GCityPartners 入口可用來找項目、判斷專業場景或整理項目需求。'
      : isOpportunity
        ? 'This opportunity appears in GCityPartners. Confirm the latest requirements and closing status on the organizer’s official page.'
        : 'Use GCityPartners to find project opportunities, fill capability gaps or build a project brief.';
    trigger.disabled = true;
    try {
      if (navigator.share) {
        await navigator.share({ title: title, text: text, url: url });
        announceShare(root, isChinese() ? '已開啟系統分享。' : 'The system share sheet is open.');
      } else {
        await copyText(url);
        announceShare(root, isChinese() ? '連結已複製。' : 'Link copied.');
      }
    } catch (error) {
      if (error && error.name === 'AbortError') {
        announceShare(root, '');
      } else {
        try {
          await copyText(url);
          announceShare(root, isChinese() ? '連結已複製。' : 'Link copied.');
        } catch (_) {
          announceShare(
            root,
            isChinese()
              ? '暫時未能分享，請直接複製瀏覽器網址。'
              : 'Unable to share. Copy the browser URL directly.'
          );
        }
      }
    } finally {
      trigger.disabled = false;
    }
  }

  function opportunityOfficialLink(card) {
    return (
      Array.prototype.slice.call(card.querySelectorAll('a[href]')).find(function (link) {
        try {
          var url = new URL(link.href, window.location.origin);
          return url.protocol === 'https:' || url.protocol === 'http:';
        } catch (_) {
          return false;
        }
      }) || null
    );
  }

  function opportunityTitle(card) {
    var node = card.querySelector('h2, h3, strong');
    return node ? String(node.textContent || '').trim() : '';
  }

  function getOpportunityBoard(root) {
    return root.querySelector('[data-gcp-opportunity-workspace] [data-gcp-opportunity-board]');
  }

  function enhanceOpportunityShares(root) {
    var board = getOpportunityBoard(root);
    if (!board) return;
    Array.prototype.forEach.call(board.querySelectorAll('.gcp-opportunity-card'), function (card) {
      if (card.querySelector('[data-gcp-share-opportunity]')) return;
      var officialLink = opportunityOfficialLink(card);
      if (!officialLink) return;
      var button = document.createElement('button');
      var title = opportunityTitle(card);
      button.className = 'gcp-btn gcp-btn--compact';
      button.type = 'button';
      button.setAttribute('data-gcp-share-opportunity', '');
      button.setAttribute('data-share-url', officialLink.href);
      button.setAttribute(
        'data-share-title',
        title ? 'GCityPartners｜' + title : 'GCityPartners｜Opportunity'
      );
      button.textContent = isChinese() ? '分享此機會' : 'Share this opportunity';
      card.appendChild(button);
    });
  }

  function bindShareControls(root, controller) {
    listen(
      root,
      root,
      'click',
      function (event) {
        var target = event.target;
        if (!target || !target.closest) return;
        var trigger = target.closest(
          '[data-gcp-share-card], [data-gcp-share-section], [data-gcp-share-opportunity]'
        );
        if (!trigger || !root.contains(trigger)) return;
        event.preventDefault();
        void shareEntry(root, trigger);
      },
      false,
      controller
    );
  }

  /* Canonical Context Bar + page-local Journey Nav own the vertical content offset.
   * Sidebar remains vertical global navigation and never contributes here. */
  function getHeaderOffset() {
    var contextBar = document.querySelector('[data-shell-surface="context-bar"]');

    var journeyNav = document.querySelector('[data-gcp-journey-nav]');

    var contextBarHeight = contextBar ? contextBar.offsetHeight : 0;

    var journeyNavHeight = journeyNav ? journeyNav.offsetHeight : 0;

    return contextBarHeight + journeyNavHeight + 16;
  }

  function smoothScrollToElement(element) {
    if (!element) return;
    var rect = element.getBoundingClientRect();
    var offset = getHeaderOffset();
    var targetY = window.pageYOffset + rect.top - offset;

    try {
      window.scrollTo({
        top: Math.max(0, targetY),
        behavior: isReducedMotion() ? 'auto' : 'smooth',
      });
    } catch (_) {
      element.scrollIntoView();
    }
  }

  function bindJourneyNavigation(root, controller) {
    var nav = root.querySelector('[data-gcp-journey-nav]');

    if (!nav) return;

    listen(
      root,
      nav,
      'click',
      function (event) {
        var target = event.target;

        if (!target || !target.closest) {
          return;
        }

        var link = target.closest('a[href^="#gcp-screen-"]');

        if (!link || !nav.contains(link)) {
          return;
        }

        var href = String(link.getAttribute('href') || '').trim();

        if (!href) return;

        var screen;

        try {
          screen = root.querySelector(href);
        } catch (_) {
          return;
        }

        if (!screen) return;

        event.preventDefault();

        var mobileDisclosure = nav.querySelector('[data-gcp-journey-mobile]');

        if (mobileDisclosure && mobileDisclosure.open) {
          mobileDisclosure.open = false;
        }

        try {
          window.history.replaceState(window.history.state, '', href);
        } catch (_) {}

        window.requestAnimationFrame(function () {
          smoothScrollToElement(screen);
        });
      },
      false,
      controller
    );
  }

  function openCard(root, id, shouldScroll) {
    if (!id) return false;
    var card = root.querySelector('#' + escapeSelector(id));
    if (!card) return false;
    if (card.tagName && card.tagName.toLowerCase() === 'details') card.open = true;
    if (shouldScroll) {
      window.requestAnimationFrame(function () {
        smoothScrollToElement(card);
      });
    }
    return true;
  }

  function bindSceneAccordionGroup(root, controller) {
    var cards = Array.prototype.slice.call(
      root.querySelectorAll('[data-gcp-entry-kind="scene"][data-gcp-entry-card]')
    );

    if (!cards.length) return;

    function hasOpenCard() {
      return cards.some(function (card) {
        return card.open;
      });
    }

    cards.forEach(function (card) {
      listen(
        root,
        card,
        'toggle',
        function () {
          if (!card.open) {
            /*
             * Capability Sidebar contract:
             * one active scene must always remain available.
             * Re-open the current card only when the user
             * would otherwise leave the right panel empty.
             */
            if (!hasOpenCard()) {
              card.open = true;
            }

            return;
          }

          cards.forEach(function (other) {
            if (other !== card) {
              other.open = false;
            }
          });
        },
        false,
        controller
      );
    });

    /*
     * Normal entry starts with the first capability active.
     * Deep-link scenario synchronization may subsequently
     * open another canonical scene; the existing toggle
     * group will then close this default card.
     */
    if (!hasOpenCard()) {
      cards[0].open = true;
    }
  }

  function syncOpportunity(root, url) {
    var type = url.searchParams.get('type') || '';
    if (!type) return;
    var link = root.querySelector('[data-gcp-opportunity-type="' + escapeSelector(type) + '"]');
    if (!link) return;
    root.querySelectorAll('[data-gcp-opportunity-type]').forEach(function (candidate) {
      if (candidate === link) candidate.setAttribute('aria-current', 'page');
      else candidate.removeAttribute('aria-current');
    });
    var card = link.closest('[data-gcp-entry-card]');
    if (card && card.tagName.toLowerCase() === 'details') card.open = true;
  }

  function redirectDuplicatedProof(root, url) {
    var scenario = normalizeScenario(url.searchParams.get('scenario') || '');
    var source = url.searchParams.get('source') || '';
    var intent = url.searchParams.get('intent') || '';
    var proofAnchor = PROOF_ANCHORS[scenario];
    if (!proofAnchor || source !== 'gproject-showcases' || intent === 'service-route') return false;
    var libraryUrl = root.getAttribute('data-proof-library-url') || '/pages/gproject-showcases';
    var destination = new URL(libraryUrl, window.location.origin);
    destination.hash = proofAnchor;
    if (destination.href === window.location.href) return false;
    window.location.replace(destination.href);
    return true;
  }

  function syncSceneEntry(root, url) {
    var scenario = normalizeScenario(url.searchParams.get('scenario') || '');
    var entryAnchor = ENTRY_ANCHORS[scenario];
    if (entryAnchor) {
      openCard(root, entryAnchor, url.hash === '#gcp-scene-gateway' || url.hash === '#overview');
    }
  }

  function syncReviewBriefEntry(root, url) {
    var intent = String(url.searchParams.get('intent') || '').toLowerCase();
    if (
      ['professional-review', 'review-brief', 'informal-preliminary-summary'].indexOf(intent) === -1
    )
      return;
    var source = url.searchParams.get('source') || 'review-brief';
    var scenario = normalizeScenario(url.searchParams.get('scenario') || '');
    var need = REVIEW_NEEDS[scenario] || 'enterprise-brief';
    var select = root.querySelector('select[name="business_need"]');
    var brief = root.querySelector('#gcp-brief-form');
    root.setAttribute('data-client-intent', intent);
    root.setAttribute('data-client-source', source);
    if (scenario) root.setAttribute('data-client-scenario', scenario);
    var precheck = root.querySelector('[name="precheck_type"]');
    var preserveBrief =
      root.getAttribute('data-review-brief-state') === 'local-draft-restored' ||
      Boolean(root.getAttribute('data-gproperty-bridge-state')) ||
      url.searchParams.get('precheck') === 'uav-3d' ||
      (precheck && precheck.value === 'uav-3d');
    if (select && !preserveBrief) {
      var optionExists = Array.prototype.some.call(select.options || [], function (option) {
        return option.value === need;
      });
      if (optionExists && select.value !== need) {
        select.value = need;
        try {
          select.dispatchEvent(new Event('change', { bubbles: true }));
        } catch (_) {}
      }
    }
    if (url.hash === '#review-brief') {
      url.hash = 'gcp-brief-form';
      try {
        window.history.replaceState(window.history.state, '', url.toString());
      } catch (_) {}
    }
    /* 💎 4. 簡報表單 (Review Brief) 智能偏置定位，避免遮擋頂部欄 */
    if (brief && (!url.hash || url.hash === '#gcp-brief-form')) {
      window.requestAnimationFrame(function () {
        smoothScrollToElement(brief);
      });
    }
  }

  function bindOpportunityRendered(root, controller) {
    listen(
      root,
      root,
      'gcitypartners:opportunities-rendered',
      function () {
        enhanceOpportunityShares(root);
      },
      false,
      controller
    );
  }

  function finalizeMenuClosed(root, menu, trigger) {
    document.body.classList.remove('is-gcp-hero-menu-open');

    if (root.__gcpHeroMenuCloseTimer) {
      window.clearTimeout(root.__gcpHeroMenuCloseTimer);
      root.__gcpHeroMenuCloseTimer = 0;
    }

    var hero = root.querySelector('[data-gcp-video-hero]');

    if (hero) {
      hero.classList.remove('is-gcp-hero-menu-open');
    }

    if (menu) {
      menu.classList.remove('is-gcp-menu-visible', 'is-gcp-menu-closing');
      menu.removeAttribute('open');
    }

    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');

      if (document.contains(trigger) && typeof trigger.focus === 'function') {
        try {
          trigger.focus({ preventScroll: true });
        } catch (_) {
          trigger.focus();
        }
      }
    }

    root.__gcpHeroMenuTrigger = null;
  }

  function closeHeroMenu(root, restoreFocus) {
    var menu = root.querySelector('[data-gcp-hero-menu]');
    var trigger = root.__gcpHeroMenuTrigger || root.querySelector('[data-gcp-hero-menu-open]');
    if (!menu) return;

    root.__gcpRestoreMenuFocus = restoreFocus !== false;

    menu.classList.remove('is-gcp-menu-visible');
    menu.classList.add('is-gcp-menu-closing');

    if (root.__gcpHeroMenuCloseTimer) {
      window.clearTimeout(root.__gcpHeroMenuCloseTimer);
    }

    if (isReducedMotion()) {
      if (menu.open && typeof menu.close === 'function') {
        menu.close();
      } else {
        finalizeMenuClosed(root, menu, restoreFocus === false ? null : trigger);
      }

      return;
    }

    root.__gcpHeroMenuCloseTimer = window.setTimeout(function () {
      root.__gcpHeroMenuCloseTimer = 0;

      if (menu.open && typeof menu.close === 'function') {
        menu.close();
        return;
      }

      finalizeMenuClosed(root, menu, restoreFocus === false ? null : trigger);
    }, 300);
  }

  function openHeroMenu(root, trigger) {
    var menu = root.querySelector('[data-gcp-hero-menu]');

    if (!menu || menu.open) return;

    if (root.__gcpHeroMenuCloseTimer) {
      window.clearTimeout(root.__gcpHeroMenuCloseTimer);
      root.__gcpHeroMenuCloseTimer = 0;
    }

    root.__gcpHeroMenuTrigger = trigger;
    trigger.setAttribute('aria-expanded', 'true');
    document.body.classList.add('is-gcp-hero-menu-open');

    var hero = root.querySelector('[data-gcp-video-hero]');

    if (hero) {
      hero.classList.add('is-gcp-hero-menu-open');
    }

    menu.classList.remove('is-gcp-menu-closing');

    if (typeof menu.showModal === 'function') {
      try {
        menu.showModal();
      } catch (_) {
        menu.setAttribute('open', '');
      }
    } else {
      menu.setAttribute('open', '');
    }

    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        menu.classList.add('is-gcp-menu-visible');

        var focusTarget =
          menu.querySelector('[data-gcp-hero-menu-close]') ||
          menu.querySelector(FOCUSABLE_SELECTOR);

        if (focusTarget && typeof focusTarget.focus === 'function') {
          focusTarget.focus();
        }
      });
    });
  }

  function cycleMenuFocus(menu, event) {
    var focusable = Array.prototype.slice
      .call(menu.querySelectorAll(FOCUSABLE_SELECTOR))
      .filter(function (node) {
        return (
          !node.hidden && node.getAttribute('aria-hidden') !== 'true' && node.offsetParent !== null
        );
      });
    if (!focusable.length) {
      event.preventDefault();
      return;
    }
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function bindHeroMenu(root, controller) {
    var menu = root.querySelector('[data-gcp-hero-menu]');
    var openButton = root.querySelector('[data-gcp-hero-menu-open]');
    if (!menu || !openButton) return;
    listen(
      root,
      openButton,
      'click',
      function () {
        openHeroMenu(root, openButton);
      },
      false,
      controller
    );
    listen(
      root,
      menu,
      'click',
      function (event) {
        var target = event.target;
        if (!target || !target.closest) return;
        if (target.closest('[data-gcp-hero-menu-close]')) {
          event.preventDefault();
          closeHeroMenu(root, true);
        } else if (target.closest('[data-gcp-hero-menu-link]')) {
          closeHeroMenu(root, false);
        }
      },
      false,
      controller
    );
    listen(
      root,
      menu,
      'cancel',
      function (event) {
        event.preventDefault();
        closeHeroMenu(root, true);
      },
      false,
      controller
    );
    listen(
      root,
      menu,
      'close',
      function () {
        var restore = root.__gcpRestoreMenuFocus !== false;
        root.__gcpRestoreMenuFocus = true;
        finalizeMenuClosed(root, menu, restore ? openButton : null);
      },
      false,
      controller
    );
    listen(
      root,
      menu,
      'keydown',
      function (event) {
        if (event.key === 'Escape') {
          event.preventDefault();
          closeHeroMenu(root, true);
        } else if (event.key === 'Tab' && typeof menu.showModal !== 'function') {
          cycleMenuFocus(menu, event);
        }
      },
      false,
      controller
    );
  }

  function setVideoState(hero, state) {
    if (hero) hero.setAttribute('data-video-state', state);
  }

  function pauseHeroVideo(video) {
    if (!video || typeof video.pause !== 'function') return;
    try {
      video.pause();
    } catch (_) {}
  }

  function playHeroVideo(hero, video) {
    if (
      !hero ||
      !video ||
      isReducedMotion() ||
      hasSaveDataPreference() ||
      document.visibilityState !== 'visible'
    )
      return;
    try {
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      var playResult = video.play();
      if (playResult && typeof playResult.then === 'function') {
        playResult
          .then(function () {
            setVideoState(hero, 'playing');
          })
          .catch(function () {
            setVideoState(hero, 'blocked');
          });
      } else {
        setVideoState(hero, 'playing');
      }
    } catch (_) {
      setVideoState(hero, 'blocked');
    }
  }

  function bindHeroVideo(root, controller) {
    var hero = root.querySelector('[data-gcp-video-hero]');
    var video = root.querySelector('[data-gcp-hero-video]');
    var heroVisible = false;
    var videoFailed = false;
    var visibilityObserver = null;

    if (!hero) return;

    if (!video) {
      setVideoState(hero, 'fallback');
      return;
    }

    if (isReducedMotion() || hasSaveDataPreference()) {
      pauseHeroVideo(video);
      setVideoState(hero, 'suppressed');
      return;
    }

    function measureHeroVisibility() {
      var rect = hero.getBoundingClientRect();
      var viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;

      return rect.bottom > 0 && rect.top < viewportHeight;
    }

    function syncVideoPlayback() {
      if (videoFailed) return;

      if (!visibilityObserver) {
        heroVisible = measureHeroVisibility();
      }

      if (heroVisible && document.visibilityState === 'visible') {
        playHeroVideo(hero, video);
        return;
      }

      pauseHeroVideo(video);
      setVideoState(hero, 'paused');
    }

    heroVisible = measureHeroVisibility();
    setVideoState(hero, 'loading');

    listen(
      root,
      video,
      'playing',
      function () {
        videoFailed = false;
        setVideoState(hero, 'playing');
      },
      false,
      controller
    );

    listen(
      root,
      video,
      'error',
      function () {
        videoFailed = true;
        pauseHeroVideo(video);
        setVideoState(hero, 'error');
      },
      false,
      controller
    );

    if (typeof window.IntersectionObserver === 'function') {
      visibilityObserver = new window.IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.target !== hero) return;

            heroVisible = entry.isIntersecting && entry.intersectionRatio > 0;

            syncVideoPlayback();
          });
        },
        {
          root: null,
          rootMargin: '0px',
          threshold: [0, 0.01, 0.1, 0.25],
        }
      );

      visibilityObserver.observe(hero);

      addFallbackCleanup(root, function () {
        if (!visibilityObserver) return;
        visibilityObserver.disconnect();
        visibilityObserver = null;
      });
    } else {
      listen(root, window, 'scroll', syncVideoPlayback, { passive: true }, controller);

      listen(root, window, 'resize', syncVideoPlayback, false, controller);
    }

    listen(
      root,
      document,
      'visibilitychange',
      function () {
        syncVideoPlayback();
      },
      false,
      controller
    );

    syncVideoPlayback();
  }

  function clampMotionValue(value) {
    return Math.max(0, Math.min(1, Number(value) || 0));
  }

  function clearHeroMotionVariables(hero) {
    if (!hero || !hero.style) return;

    [
      '--gcp-hero-progress',
      '--gcp-hero-exit-progress',
      '--gcp-hero-video-scale',
      '--gcp-hero-scrim-opacity',
      '--gcp-hero-nav-y',
      '--gcp-hero-nav-opacity',
      '--gcp-hero-content-y',
      '--gcp-hero-content-opacity',
    ].forEach(function (property) {
      hero.style.removeProperty(property);
    });
  }

  function clearHandoffMotionVariables(root) {
    if (!root || !root.style) return;

    [
      '--gcp-handoff-origin-opacity',
      '--gcp-handoff-parcel-opacity',
      '--gcp-handoff-route-opacity',
      '--gcp-handoff-target-opacity',
    ].forEach(function (property) {
      root.style.removeProperty(property);
    });
  }

  /* 💎 3. 三端斷點對齊：統一為 768px 標準平板/桌面分界線，確保 dvh 視口高幀率漸變 */
  function bindHeroScrollProgress(root, controller) {
    var screen = root.querySelector('[data-gcp-screen="00"]');
    var hero = root.querySelector('[data-gcp-video-hero]');
    var frame = 0;
    var desktopQuery = window.matchMedia
      ? window.matchMedia('(min-width: 768px) and (min-height: 600px)')
      : null;

    if (!screen || !hero || isReducedMotion()) return;

    function measure() {
      var rect;
      var viewportHeight;
      var progress;
      var exitProgress;
      var handoffArrival;
      var handoffRoute;
      var handoffTarget;

      frame = 0;

      if (desktopQuery && !desktopQuery.matches) {
        clearHeroMotionVariables(hero);
        clearHandoffMotionVariables(root);
        return;
      }

      rect = screen.getBoundingClientRect();
      viewportHeight = window.innerHeight || document.documentElement.clientHeight || 1;

      /* Screen 00 is a viewport chapter. Track its exit across one viewport,
       * not overflow height; a 100dvh screen otherwise collapses travel to 1px. */
      progress = clampMotionValue(-rect.top / Math.max(viewportHeight, 1));

      exitProgress = clampMotionValue((progress - 0.58) / 0.42);

      handoffArrival = clampMotionValue((exitProgress - 0.88) / 0.12);

      handoffRoute = clampMotionValue((exitProgress - 0.93) / 0.07);

      handoffTarget = clampMotionValue((exitProgress - 0.965) / 0.035);

      hero.style.setProperty('--gcp-hero-progress', progress.toFixed(4));

      hero.style.setProperty('--gcp-hero-exit-progress', exitProgress.toFixed(4));

      root.style.setProperty('--gcp-handoff-origin-opacity', (handoffArrival * 0.34).toFixed(4));

      root.style.setProperty('--gcp-handoff-parcel-opacity', (handoffArrival * 0.28).toFixed(4));

      root.style.setProperty('--gcp-handoff-route-opacity', (handoffRoute * 0.24).toFixed(4));

      root.style.setProperty('--gcp-handoff-target-opacity', (handoffTarget * 0.52).toFixed(4));

      hero.style.setProperty('--gcp-hero-video-scale', (1 + progress * 0.035).toFixed(4));

      hero.style.setProperty('--gcp-hero-scrim-opacity', (0.9 + exitProgress * 0.1).toFixed(4));

      hero.style.setProperty('--gcp-hero-nav-y', (-exitProgress * 12).toFixed(2) + 'px');

      hero.style.setProperty('--gcp-hero-nav-opacity', (1 - exitProgress * 0.72).toFixed(4));

      hero.style.setProperty('--gcp-hero-content-y', (-exitProgress * 44).toFixed(2) + 'px');

      hero.style.setProperty('--gcp-hero-content-opacity', (1 - exitProgress * 0.94).toFixed(4));
    }

    function requestMeasure() {
      if (frame) return;
      frame = window.requestAnimationFrame(measure);
    }

    listen(root, window, 'scroll', requestMeasure, { passive: true }, controller);

    listen(root, window, 'resize', requestMeasure, false, controller);

    listen(root, window, 'orientationchange', requestMeasure, false, controller);

    addFallbackCleanup(root, function () {
      if (frame) {
        window.cancelAnimationFrame(frame);
        frame = 0;
      }

      clearHeroMotionVariables(hero);
      clearHandoffMotionVariables(root);
    });

    measure();
  }

  function bindBriefContextHandoffs(root, controller) {
    var form = (root.hasAttribute('data-gcp-public-tabs') ? document : root).querySelector(
      '[data-gcp-brief-only-form]'
    );

    if (!form || !form.elements) return;

    function setField(name, value, overwrite) {
      var field = form.elements.namedItem(name);
      var next = String(value || '').trim();

      if (!field || !next) return;

      if (overwrite === true || !String(field.value || '').trim()) {
        field.value = next;
      }
    }

    listen(
      root,
      root,
      'click',
      function (event) {
        var link = event.target.closest && event.target.closest('[data-gcp-brief-context]');

        if (!link || !root.contains(link)) return;

        var card = link.closest && link.closest('[data-gcp-entry-card]');

        var contextKind = String(link.getAttribute('data-gcp-brief-context') || '').trim();

        var lane = String(
          link.getAttribute('data-gcp-context-service-lane') ||
            (card && card.getAttribute('data-gcp-service-lane')) ||
            ''
        ).trim();

        var contextId = String(
          link.getAttribute('data-gcp-context-id') ||
            (card &&
              (card.getAttribute('data-gcp-scene-handle') ||
                card.getAttribute('data-gcp-opportunity-entry') ||
                card.getAttribute('data-gcp-opportunity-id'))) ||
            ''
        ).trim();

        var opportunityType = String(
          link.getAttribute('data-gcp-opportunity-type') ||
            (card && card.getAttribute('data-gcp-opportunity-entry')) ||
            ''
        ).trim();

        if (contextKind === 'scene-gateway' && !lane) {
          var activeScene = root.querySelector('[data-gcp-entry-kind="scene"][open]');

          if (activeScene) {
            lane = String(activeScene.getAttribute('data-gcp-service-lane') || '').trim();

            contextId = String(activeScene.getAttribute('data-gcp-scene-handle') || '').trim();
          }
        }

        if (lane) {
          setField('service_lane', lane, true);
        }

        if (contextId) {
          setField('source_card_id', contextId, true);
        }

        if (contextKind === 'opportunity' || contextKind === 'opportunity-record') {
          setField('source', 'gcitypartners-opportunity', true);

          setField(
            'source_context',
            opportunityType ? 'opportunity:' + opportunityType : 'opportunity',
            true
          );

          setField(
            'source_card_type',
            contextKind === 'opportunity-record' ? 'opportunity_record' : 'opportunity_category',
            true
          );

          if (opportunityType === 'tender') {
            setField('business_need', 'tender-radar', false);
          }
        } else if (contextKind === 'scene' || contextKind === 'scene-gateway') {
          setField('source', 'gcitypartners-capability-sharing', true);

          setField('source_context', 'capability-sharing', true);

          setField('source_card_type', 'professional_scene', true);
        }

        var resourceMode = root.querySelector('[data-gcp-resource-mode]');

        var resourceType = root.querySelector('[data-gcp-resource-type]');

        if (resourceMode && String(resourceMode.value || '').trim()) {
          setField('resource_mode', resourceMode.value, true);
        }

        if (resourceType && String(resourceType.value || '').trim()) {
          setField('resource_type', resourceType.value, true);

          setField('source', 'gcitypartners-resource-sharing', true);

          setField('source_context', 'resource-sharing', true);

          setField('source_card_type', 'resource_sharing', true);
        }
      },
      false,
      controller
    );
  }

  function bindLifecycleNavigator(root, controller) {
    var lifecycle = root.querySelector('[data-gcitypartners-lifecycle]');

    if (!lifecycle) return;

    var stages = Array.prototype.slice.call(
      lifecycle.querySelectorAll('[data-gcp-lifecycle-stage]')
    );

    var briefForm = (root.hasAttribute('data-gcp-public-tabs') ? document : root).querySelector(
      '[data-gcp-brief-only-form]'
    );

    var stageField =
      briefForm && briefForm.elements ? briefForm.elements.namedItem('project_stage') : null;

    var briefLink = lifecycle.querySelector('[data-gcp-lifecycle-brief-link]');

    var status = lifecycle.querySelector('[data-gcp-lifecycle-status]');

    var syncTimer = 0;

    if (!stages.length || !stageField) return;

    function announce(message) {
      if (status) {
        status.textContent = message || '';
      }
    }

    function stageValue(stage) {
      return String(stage.getAttribute('data-gcp-project-stage') || '').trim();
    }

    function stageLabel(stage) {
      var label = stage.querySelector('strong');

      return label ? String(label.textContent || '').trim() : '';
    }

    function setVisualState(stage) {
      stages.forEach(function (candidate) {
        var selected = candidate === stage;

        candidate.setAttribute('aria-pressed', selected ? 'true' : 'false');

        var item = candidate.closest('[data-gcp-lifecycle-stage-item]');

        if (item) {
          item.classList.toggle('is-current', selected);
        }
      });
    }

    function selectStage(stage, writeField) {
      var value = stageValue(stage);

      if (!value) return;

      setVisualState(stage);

      lifecycle.setAttribute('data-gcp-current-stage', value);

      lifecycle.removeAttribute('data-gcp-stage-needed');

      root.setAttribute('data-gcp-project-stage', value);

      if (writeField !== false) {
        stageField.value = value;

        try {
          stageField.dispatchEvent(new Event('change', { bubbles: true }));
        } catch (_) {}
      }

      announce(
        isChinese() ? '目前階段：' + stageLabel(stage) : 'Current stage: ' + stageLabel(stage)
      );
    }

    function syncFromField() {
      var value = String(stageField.value || '').trim();

      if (!value) return;

      var matchingStage = stages.find(function (stage) {
        return stageValue(stage) === value;
      });

      if (matchingStage) {
        selectStage(matchingStage, false);
      }
    }

    stages.forEach(function (stage) {
      listen(
        root,
        stage,
        'click',
        function () {
          selectStage(stage, true);
        },
        false,
        controller
      );
    });

    listen(root, stageField, 'change', syncFromField, false, controller);

    if (briefLink) {
      listen(
        root,
        briefLink,
        'click',
        function (event) {
          if (String(stageField.value || '').trim()) {
            return;
          }

          event.preventDefault();

          lifecycle.setAttribute('data-gcp-stage-needed', 'true');

          announce(
            isChinese() ? '請先選擇目前項目階段。' : 'Select the current project stage first.'
          );

          if (stages[0] && typeof stages[0].focus === 'function') {
            stages[0].focus();
          }
        },
        false,
        controller
      );
    }

    syncTimer = window.setTimeout(syncFromField, 0);

    addFallbackCleanup(root, function () {
      if (syncTimer) {
        window.clearTimeout(syncTimer);
        syncTimer = 0;
      }
    });
  }

  function syncJourneyNav(root, screen) {
    if (!root || !screen) return;

    var nav = root.querySelector('[data-gcp-journey-nav]');

    if (!nav) return;

    var screenValue = String(screen.getAttribute('data-gcp-screen') || '').trim();

    var intent = String(screen.getAttribute('data-gcp-journey-intent') || '').trim();

    var label = String(screen.getAttribute('data-gcp-nav-label') || '').trim();

    if (!screenValue || !intent) return;

    nav.setAttribute('data-gcp-current-screen', screenValue);

    nav.setAttribute('data-gcp-current-intent', intent);

    var stepNode = nav.querySelector('[data-gcp-journey-step]');

    var labelNode = nav.querySelector('[data-gcp-journey-label]');

    var intentNode = nav.querySelector(
      '.gcp-journey-nav__intent' + '[data-gcp-journey-intent="' + escapeSelector(intent) + '"]'
    );

    var intentLabel = intentNode ? String(intentNode.textContent || '').trim() : '';

    var mobileIntentNode = nav.querySelector('[data-gcp-journey-mobile-intent]');

    var mobileProgressNode = nav.querySelector('[data-gcp-journey-mobile-progress]');

    var mobileLabelNode = nav.querySelector('[data-gcp-journey-mobile-label]');

    if (stepNode) {
      stepNode.textContent = screenValue;
    }

    if (labelNode) {
      labelNode.textContent = label;
    }

    if (mobileIntentNode) {
      mobileIntentNode.textContent = intentLabel;
    }

    if (mobileProgressNode) {
      mobileProgressNode.textContent = screenValue + ' / 08';
    }

    if (mobileLabelNode) {
      mobileLabelNode.textContent = label;
    }

    Array.prototype.forEach.call(
      nav.querySelectorAll('.gcp-journey-nav__intent' + '[data-gcp-journey-intent]'),
      function (candidate) {
        var isCurrent = candidate.getAttribute('data-gcp-journey-intent') === intent;

        if (isCurrent) {
          candidate.setAttribute('aria-current', 'location');
        } else {
          candidate.removeAttribute('aria-current');
        }
      }
    );

    Array.prototype.forEach.call(
      nav.querySelectorAll('[data-gcp-journey-mobile-screen]'),
      function (candidate) {
        var isCurrent = candidate.getAttribute('data-gcp-journey-mobile-screen') === screenValue;

        if (isCurrent) {
          candidate.setAttribute('aria-current', 'page');
        } else {
          candidate.removeAttribute('aria-current');
        }
      }
    );
  }

  function bindMobileJourneyDisclosure(root, controller) {
    var nav = root.querySelector('[data-gcp-journey-nav]');

    if (!nav) return;

    var disclosure = nav.querySelector('[data-gcp-journey-mobile]');

    var summary = nav.querySelector('[data-gcp-journey-mobile-summary]');

    if (!disclosure || !summary) return;

    function syncExpanded() {
      summary.setAttribute('aria-expanded', disclosure.open ? 'true' : 'false');
    }

    listen(root, disclosure, 'toggle', syncExpanded, false, controller);

    listen(
      root,
      nav,
      'click',
      function (event) {
        var target = event.target;

        if (!target || !target.closest) {
          return;
        }

        var link = target.closest('[data-gcp-journey-mobile-screen]');

        if (!link || !nav.contains(link)) {
          return;
        }

        disclosure.open = false;
        syncExpanded();
      },
      false,
      controller
    );

    listen(
      root,
      nav,
      'keydown',
      function (event) {
        if (event.key !== 'Escape' || !disclosure.open) {
          return;
        }

        event.preventDefault();

        disclosure.open = false;
        syncExpanded();

        if (typeof summary.focus === 'function') {
          summary.focus();
        }
      },
      false,
      controller
    );

    syncExpanded();
  }

  function bindGatewayScreenMotion(root) {
    var shell = root.querySelector('.gcp-shell');
    var screens = [];
    var ratios = {};
    var observer = null;

    if (shell) {
      Array.prototype.forEach.call(shell.children, function (screen) {
        if (!screen.matches || !screen.matches('[data-gcp-screen]')) {
          return;
        }

        screens.push(screen);
      });
    }

    if (!screens.length) return;

    function screenName(screen) {
      return screen.getAttribute('data-gcp-screen') || '';
    }

    function setActiveScreen(screen) {
      var value = screenName(screen);

      if (!value) return;

      root.setAttribute('data-gcp-active-screen', value);

      syncJourneyNav(root, screen);
    }

    screens[0].setAttribute('data-gcp-screen-visible', 'true');

    setActiveScreen(screens[0]);

    if (isReducedMotion() || typeof window.IntersectionObserver !== 'function') {
      screens.forEach(function (screen) {
        screen.setAttribute('data-gcp-screen-visible', 'true');
      });

      return;
    }

    observer = new window.IntersectionObserver(
      function (entries) {
        var bestScreen = null;
        var bestRatio = 0;

        entries.forEach(function (entry) {
          var index = screens.indexOf(entry.target);

          if (index === -1) return;

          ratios[index] = entry.isIntersecting ? entry.intersectionRatio : 0;

          if (entry.isIntersecting && entry.intersectionRatio >= 0.1) {
            entry.target.setAttribute('data-gcp-screen-visible', 'true');
          }
        });

        screens.forEach(function (screen, index) {
          var ratio = ratios[index] || 0;

          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestScreen = screen;
          }
        });

        /* 💎 2. 全域事件連動：向 Sidebar 及首頁廣播 Screen 切換，即時同步 Active 狀態 */
        if (bestScreen) {
          setActiveScreen(bestScreen);
          try {
            document.dispatchEvent(
              new CustomEvent('littleg:gateway-screen-change', {
                detail: {
                  screen: screenName(bestScreen),
                  instance: root.getAttribute('data-section-instance') || '',
                },
                bubbles: true,
              })
            );
          } catch (_) {}
        }
      },
      {
        root: null,
        rootMargin: '-20% 0px -42% 0px',
        threshold: [0, 0.1, 0.25, 0.5, 0.75],
      }
    );

    screens.forEach(function (screen) {
      observer.observe(screen);
    });

    addFallbackCleanup(root, function () {
      if (observer) {
        observer.disconnect();
        observer = null;
      }

      root.removeAttribute('data-gcp-active-screen');

      if (screens[0]) {
        syncJourneyNav(root, screens[0]);
      }

      screens.forEach(function (screen) {
        screen.removeAttribute('data-gcp-screen-visible');
      });
    });
  }

  function initHeroMotion(root) {
    var hero = root.querySelector('[data-gcp-video-hero]');
    if (!hero) return;

    if (isReducedMotion()) {
      root.removeAttribute('data-gcp-motion-enabled');
      hero.classList.add('is-gcp-hero-ready');
      return;
    }

    root.setAttribute('data-gcp-motion-enabled', 'true');

    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        if (document.contains(hero)) {
          hero.classList.add('is-gcp-hero-ready');
        }
      });
    });
  }

  function teardown(root) {
    if (!(root instanceof HTMLElement)) return;

    var heroMenu = root.querySelector('[data-gcp-hero-menu]');

    if (root.__gcpHeroMenuCloseTimer) {
      window.clearTimeout(root.__gcpHeroMenuCloseTimer);
      root.__gcpHeroMenuCloseTimer = 0;
    }

    finalizeMenuClosed(root, heroMenu, null);

    pauseHeroVideo(root.querySelector('[data-gcp-hero-video]'));
    if (
      root.__gcpEntryAbortController &&
      typeof root.__gcpEntryAbortController.abort === 'function'
    ) {
      root.__gcpEntryAbortController.abort();
    }
    (root.__gcpEntryFallbackCleanups || []).forEach(function (cleanup) {
      try {
        cleanup();
      } catch (_) {}
    });
    root.__gcpEntryAbortController = null;
    root.__gcpEntryFallbackCleanups = [];
    root.__gcpHeroMenuTrigger = null;
    root.__gcpRestoreMenuFocus = true;
    root.removeAttribute('data-entry-router-ready');
    root.removeAttribute('data-entry-runtime-owner');
    root.removeAttribute('data-gcp-motion-enabled');
    clearHandoffMotionVariables(root);

    Array.prototype.forEach.call(root.querySelectorAll('[data-gcp-screen]'), function (screen) {
      screen.removeAttribute('data-gcp-screen-visible');
    });

    var hero = root.querySelector('[data-gcp-video-hero]');

    if (hero) {
      clearHeroMotionVariables(hero);

      hero.classList.remove('is-gcp-hero-ready', 'is-gcp-hero-menu-open');
    }
  }

  function bindPublicTabs(root, controller) {
    var tabs = ['overview', 'opportunities', 'solutions', 'capabilities'];
    var nav =
      document.querySelector('[data-gcp-public-nav]') ||
      root.querySelector('[data-gcp-public-nav]');
    if (!nav) return;
    var panels = Array.prototype.slice.call(document.querySelectorAll('[data-gcp-public-panel]'));
    var links = Array.prototype.slice.call(nav.querySelectorAll('[data-gcp-public-tab]'));
    var active = 'overview';
    var legacy = {
      '00': 'overview',
      '01': 'opportunities',
      '02': 'solutions',
      '03': 'capabilities',
      '04': 'opportunities',
      '05': 'solutions',
      '06': 'solutions',
      '07': 'solutions',
    };
    function hashTarget(url) {
      var hash;
      try {
        hash = decodeURIComponent(url.hash.slice(1));
      } catch (_) {
        return null;
      }
      var screen = /^gcp-screen-(0[0-7])$/.exec(hash);
      if (screen)
        return {
          tab: legacy[screen[1]],
          node:
            document.getElementById(hash) ||
            panels.find(function (panel) {
              return panel.getAttribute('data-gcp-public-panel') === legacy[screen[1]];
            }),
        };
      if (hash === 'review-brief')
        return { tab: 'solutions', node: document.getElementById('gcp-brief-form') };
      if (hash === 'platform') {
        const solutionsPanel = panels.find(function (panel) {
          return panel.getAttribute('data-gcp-public-panel') === 'solutions';
        });
        return solutionsPanel ? { tab: 'solutions', node: solutionsPanel } : null;
      }
      var node = document.getElementById(hash);
      var panel = node && node.closest('[data-gcp-public-panel]');
      if (panel) return { tab: panel.getAttribute('data-gcp-public-panel'), node: node };
      if (hash === 'overview' || hash === 'opportunities')
        return {
          tab: hash,
          node: panels.find(function (panel) {
            return panel.getAttribute('data-gcp-public-panel') === hash;
          }),
        };
      return null;
    }
    function show(value, focus, target) {
      active = tabs.indexOf(value) > -1 ? value : 'overview';
      var previouslyFocused = document.activeElement;
      panels.forEach(function (panel) {
        panel.hidden = panel.getAttribute('data-gcp-public-panel') !== active;
      });
      links.forEach(function (link) {
        var selected = link.getAttribute('data-gcp-public-tab') === active;
        if (selected) link.setAttribute('aria-current', 'page');
        else link.removeAttribute('aria-current');
      });
      root.setAttribute('data-gcp-active-tab', active);
      if (
        focus ||
        (previouslyFocused &&
          previouslyFocused.closest &&
          previouslyFocused.closest('[data-gcp-public-panel][hidden]'))
      )
        links[tabs.indexOf(active)].focus({ preventScroll: true });
      if (target)
        window.requestAnimationFrame(function () {
          if (root.isConnected && target.isConnected && !target.closest('[hidden]'))
            smoothScrollToElement(target);
        });
      document.dispatchEvent(
        new CustomEvent('gcitypartners:public-tab-change', { detail: { tab: active } })
      );
    }
    function sync(shouldScroll) {
      var url = new URL(window.location.href);
      var hash = hashTarget(url);
      var requested = url.searchParams.get('tab');
      var canonicalRequested = requested === 'delivery' ? 'solutions' : requested;
      var reviewIntent =
        !requested &&
        /^(professional-review|review-brief|informal-preliminary-summary)$/.test(
          url.searchParams.get('intent') || ''
        );
      var next = hash
        ? hash.tab
        : tabs.indexOf(canonicalRequested) > -1
          ? canonicalRequested
          : reviewIntent
            ? 'solutions'
            : 'overview';
      var legacyHash = /^#gcp-screen-0[0-7]$/.test(url.hash);
      if (legacyHash) url.hash = '';
      else if (url.hash === '#review-brief') url.hash = 'gcp-brief-form';
      if (requested !== next || legacyHash || window.location.hash === '#review-brief') {
        url.searchParams.set('tab', next);
        window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
      }
      show(next, false, shouldScroll && hash ? hash.node : null);
    }
    listen(
      root,
      nav,
      'click',
      function (event) {
        var link = event.target.closest('[data-gcp-public-tab]');
        if (
          !link ||
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey
        )
          return;
        event.preventDefault();
        var value = link.getAttribute('data-gcp-public-tab');
        var url = new URL(window.location.href);
        url.searchParams.set('tab', value);
        url.hash = '';
        if (active !== value || window.location.hash)
          window.history.pushState(window.history.state, '', url.pathname + url.search);
        show(value, true, null);
      },
      false,
      controller
    );
    listen(
      root,
      nav,
      'keydown',
      function (event) {
        var link = event.target.closest('[data-gcp-public-tab]');
        if (!link) return;
        var index = links.indexOf(link),
          next;
        if (event.key === 'ArrowRight') next = (index + 1) % links.length;
        else if (event.key === 'ArrowLeft') next = (index + links.length - 1) % links.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = links.length - 1;
        else return;
        event.preventDefault();
        links[next].focus();
      },
      false,
      controller
    );
    listen(
      root,
      document,
      'click',
      function (event) {
        var link = event.target.closest && event.target.closest('a[href]');
        if (
          !link ||
          event.defaultPrevented ||
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey ||
          link.target === '_blank'
        )
          return;
        var url;
        try {
          url = new URL(link.href, window.location.origin);
        } catch (_) {
          return;
        }
        if (
          url.origin !== window.location.origin ||
          url.pathname !== window.location.pathname ||
          !url.hash
        )
          return;
        var target = hashTarget(url);
        if (!target) return;
        event.preventDefault();
        var current = new URL(window.location.href);
        url.searchParams.forEach(function (value, key) {
          current.searchParams.set(key, value);
        });
        current.searchParams.set('tab', target.tab);
        current.hash = /^#gcp-screen-0[0-7]$/.test(url.hash)
          ? ''
          : url.hash === '#review-brief'
            ? '#gcp-brief-form'
            : url.hash;
        window.history.pushState(
          window.history.state,
          '',
          current.pathname + current.search + current.hash
        );
        show(target.tab, false, target.node);
      },
      false,
      controller
    );
    listen(
      root,
      window,
      'popstate',
      function () {
        sync(true);
      },
      false,
      controller
    );
    listen(
      root,
      window,
      'hashchange',
      function () {
        sync(true);
      },
      false,
      controller
    );
    addFallbackCleanup(root, function () {
      panels.forEach(function (panel) {
        panel.hidden = false;
      });
    });
    function account() {
      var link = document.querySelector('[data-gcp-native-account-entry]');
      if (!link) return;
      var owner = window.LittleGWebSession;
      var state =
        owner && typeof owner.getState === 'function' ? owner.getState() : { status: 'error' };
      var authenticated = state.status === 'authenticated';
      var booting = state.status === 'booting' || state.status === 'unknown';
      var hans = /^zh-(cn|hans|sg)(?:-|$)/i.test(document.documentElement.lang || '');
      var label = booting
        ? hans
          ? '正在检查账户…'
          : isChinese()
            ? '正在檢查帳戶…'
            : 'Checking account…'
        : authenticated
          ? isChinese()
            ? '我的工作台'
            : 'My Workspace'
          : state.status === 'error'
            ? hans
              ? '重试登录'
              : isChinese()
                ? '重試登入'
                : 'Sign in'
            : hans
              ? '登录'
              : isChinese()
                ? '登入'
                : 'Sign In';
      link.textContent = label;
      link.setAttribute('data-native-session-state', state.status);
      link.setAttribute('aria-disabled', booting ? 'true' : 'false');
      link.href = authenticated ? '/pages/gcitypartners-dashboard' : '/pages/gcitypartners-sign-in';
    }
    listen(root, document, 'littleg:web-session-change', account, false, controller);
    listen(root, document, 'gcitypartners:public-tab-change', account, false, controller);
    listen(
      root,
      document,
      'click',
      function (event) {
        var link = event.target.closest && event.target.closest('[data-gcp-native-account-entry]');
        if (link && link.getAttribute('aria-disabled') === 'true') event.preventDefault();
      },
      false,
      controller
    );
    account();
    if (window.LittleGWebSession) window.LittleGWebSession.ready().then(account, account);
    sync(true);
  }

  /** @param {HTMLElement} root */
  function init(root) {
    if (!root || root.getAttribute('data-entry-router-ready') === VERSION) return;
    teardown(root);
    var controller = createAbortController();
    root.__gcpEntryAbortController = controller;
    root.__gcpEntryFallbackCleanups = [];
    root.__gcpRestoreMenuFocus = true;
    root.setAttribute('data-entry-router-ready', VERSION);
    root.setAttribute('data-entry-runtime-owner', 'assets/gcitypartners-entry-gateway.js');
    bindSceneAccordionGroup(root, controller);
    bindShareControls(root, controller);
    bindOpportunityRendered(root, controller);
    if (root.hasAttribute('data-gcp-public-tabs')) {
      bindBriefContextHandoffs(root, controller);
      bindLifecycleNavigator(root, controller);
      bindPublicTabs(root, controller);
      var publicUrl = new URL(window.location.href);
      syncOpportunity(root, publicUrl);
      syncSceneEntry(root, publicUrl);
      var publicBrief = document.querySelector(
        '[data-gcp-public-panel="solutions"][data-gcitypartners-page-system]'
      );
      if (publicBrief && root.getAttribute('data-gcp-active-tab') === 'solutions')
        syncReviewBriefEntry(publicBrief, publicUrl);
      return;
    }
    bindHeroMenu(root, controller);
    initHeroMotion(root);
    bindHeroScrollProgress(root, controller);

    bindBriefContextHandoffs(root, controller);

    bindLifecycleNavigator(root, controller);

    bindMobileJourneyDisclosure(root, controller);

    bindJourneyNavigation(root, controller);

    bindGatewayScreenMotion(root);
    bindHeroVideo(root, controller);
    enhanceOpportunityShares(root);
    var url;
    try {
      url = new URL(window.location.href);
    } catch (_) {
      return;
    }
    if (redirectDuplicatedProof(root, url)) return;
    syncOpportunity(root, url);
    syncSceneEntry(root, url);
    syncReviewBriefEntry(root, url);
  }

  function initAll(scope) {
    var target = scope || document;
    if (target.matches && target.matches(ROOT_SELECTOR)) init(target);
    Array.prototype.forEach.call(
      target.querySelectorAll ? target.querySelectorAll(ROOT_SELECTOR) : [],
      init
    );
  }

  function teardownAll(scope) {
    var target = scope || document;
    if (target.matches && target.matches(ROOT_SELECTOR)) teardown(target);
    Array.prototype.forEach.call(
      target.querySelectorAll ? target.querySelectorAll(ROOT_SELECTOR) : [],
      teardown
    );
  }

  if (!window.__gcpEntryLifecycleInstalled) {
    window.__gcpEntryLifecycleInstalled = true;
    document.addEventListener('shopify:section:load', function (event) {
      initAll(event.target || document);
    });
    document.addEventListener('shopify:section:unload', function (event) {
      teardownAll(event.target || document);
    });
    if (document.readyState === 'loading') {
      document.addEventListener(
        'DOMContentLoaded',
        function () {
          initAll(document);
        },
        { once: true }
      );
    }
  }
  if (document.readyState !== 'loading') initAll(document);
})(window, document);
